import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CheckCircle } from "lucide-react";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Bitte geben Sie Ihre E-Mail-Adresse ein");
      return;
    }

    setIsLoading(true);
    try {
      await forgotPassword(email);
      setIsSuccess(true);
    } catch (error) {
      toast.error("Ein Fehler ist aufgetreten");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="forgot-password-page">
      {/* Background with abstract shapes */}
      <div className="fixed inset-0 bg-[#f2f2f2] -z-10">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c3d7e8" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#a8d4e6" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#d4e5f0" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#b8d4e8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <polygon points="0,400 600,200 400,600 0,800" fill="url(#grad1)" />
          <polygon points="1920,0 1400,300 1600,700 1920,400" fill="url(#grad2)" />
          <polygon points="800,900 1200,700 1100,1080 700,1080" fill="url(#grad1)" />
        </svg>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-[440px]">
          {/* Card */}
          <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.2)] p-11">
            {/* Logo */}
            <div className="mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexipfleg-login/artifacts/d5s7aion_image.png" 
                alt="Holding" 
                className="h-14"
                data-testid="holding-logo"
              />
            </div>

            {isSuccess ? (
              <>
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-6 w-6 text-[#107c10] mr-2" />
                  <h1 className="text-2xl font-semibold text-[#1b1b1b]" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                    E-Mail gesendet
                  </h1>
                </div>

                <p className="text-sm text-[#666] mb-6">
                  Wenn ein Konto mit <strong>{email}</strong> existiert, haben wir Ihnen einen Link zum Zurücksetzen Ihres Kennworts gesendet.
                </p>

                <div className="flex justify-end space-x-2">
                  <Link to="/login">
                    <Button 
                      variant="outline"
                      className="rounded-none px-8 h-9 text-[15px] font-normal border-[#8c8c8c]"
                      data-testid="back-to-login-button"
                    >
                      Zurück zur Anmeldung
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center text-sm text-[#1b1b1b] hover:underline mb-4" data-testid="back-link">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Zurück
                </Link>

                <h1 className="text-2xl font-semibold text-[#1b1b1b] mb-2" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                  Konto wiederherstellen
                </h1>

                <p className="text-sm text-[#666] mb-4">
                  Geben Sie die E-Mail-Adresse ein, die Sie für Ihr Konto verwenden.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <Input
                      type="email"
                      placeholder="E-Mail"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                      disabled={isLoading}
                      autoFocus
                      data-testid="forgot-password-email-input"
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Link to="/login">
                      <Button 
                        type="button"
                        variant="outline"
                        className="rounded-none px-8 h-9 text-[15px] font-normal border-[#8c8c8c]"
                      >
                        Abbrechen
                      </Button>
                    </Link>
                    <Button 
                      type="submit"
                      className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                      disabled={isLoading}
                      data-testid="forgot-password-submit-button"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Weiter"
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end items-center p-4 text-xs text-[#666] space-x-6">
        <a href="#" className="hover:underline">Nutzungsbedingungen</a>
        <a href="#" className="hover:underline">Datenschutz und Cookies</a>
        <button className="hover:underline">...</button>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
